import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import moment from 'moment';
import { PendingCommunicationsHistory, CommunicationsPendingHistoryForm } from 'types';

import HistorisationPendingList from 'components/templates/historisation-pending-list';
import Layout from 'components/templates/layout';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import {
  historicCommunicationActions,
  historicCommunicationSelectors,
  SituationsCommunicationsActions,
  SituationsCommunicationsSelectors,
  MasksConfigActions,
  historicPendingCommunicationActions,
  historicPendingCommunicationSelectors,
  UserInfoSelectors,
} from 'common/config/store';
import HistorisationPendingFilterForm from 'components/templates/form-historisation-pending-list';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useIntl } from 'common/config/i18n';
import { isRequestError } from 'common/utils/error';
import MessageModal from 'components/organisms/message-modal';
import { htmlParser } from 'common/utils/html-parser';
import useUrlParams from 'hooks/use-urlParams';

const { StyledListWrapper, StyledListTitleWrapper, StyledListBoldWrapper } = StyledListUtil;

const HistorisationPending: React.FC = () => {
  const { t, currentLocale } = useIntl();
  const dispatch = useDispatch();
  const urlSearchParams = useUrlParams({ clearAfterRead: true });

  const [modalVisible, setModalVisible] = useState(false);
  const [messagesToDelete, setmessagesToDelete] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filterValues, setFilterValues] = useState<CommunicationsPendingHistoryForm>({});

  const historicPendingCommData = useSelector(historicPendingCommunicationSelectors.dataSelector) || {};
  const historicPendingCommStatus = useSelector(historicPendingCommunicationSelectors.statusSelector);
  const senders = useSelector(historicCommunicationSelectors.sendersSelector);
  const situations = useSelector(SituationsCommunicationsSelectors.situationsCommunicationsSelector);
  const canWriteDefferedCommunications = useSelector(UserInfoSelectors.hasWriteAccess('Deffered-Communications'));
  const error = useSelector(historicPendingCommunicationSelectors.errorSelector);
  const errorType = useSelector(historicPendingCommunicationSelectors.errorTypeSelector);

  useEffect(() => {
    // We wait untill urlSearchParams has value
    if (urlSearchParams !== null) {
      const { serviceName = undefined, circulationDate = undefined } = urlSearchParams;

      setFilterValues({
        serviceName: serviceName || undefined,
        creationStartDate: circulationDate ? undefined : moment().subtract(10, 'days').format('YYYY-MM-DD'),
        creationEndDate: circulationDate ? undefined : moment().format('YYYY-MM-DD'),
        arrivalTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
        departureTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
        pageNumber: 1,
        maxResultPage: 20,
      });

      dispatch(SituationsCommunicationsActions.fetchCommunicationsSituations());
      dispatch(MasksConfigActions.fetchMasksConfig());
      dispatch(historicCommunicationActions.fetchHistoricCommunicationSenders());
      dispatch(
        historicPendingCommunicationActions.fetchHistoricPendingCommunication({
          serviceName: serviceName || undefined,
          creationStartDate: circulationDate ? undefined : moment().subtract(10, 'days').format('YYYY-MM-DD'),
          creationEndDate: circulationDate ? undefined : moment().format('YYYY-MM-DD'),
          arrivalTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
          departureTime: circulationDate ? moment(circulationDate).format('YYYY-MM-DD') : undefined,
          pageNumber: 1,
          maxResultPage: 20,
        })
      );
    }
  }, [dispatch, currentLocale, urlSearchParams]);

  useEffect(() => {
    if (errorType && errorType === 'delete') setShowDeleteModal(true);
  }, [errorType]);

  const onModalConfirm = (shouldRemove: boolean) => {
    if (shouldRemove) {
      dispatch(historicPendingCommunicationActions.deleteHistoricPendingCommunication(messagesToDelete));
      dispatch(historicPendingCommunicationActions.fetchHistoricPendingCommunication(filterValues));
    }
    setModalVisible(false);
  };

  const onDeleteHandler = () => {
    if (messagesToDelete.length > 0) {
      setModalVisible(true);
    }
  };

  const prepareWarningMessage = () => {
    const warningMessage: string[] = [];
    messagesToDelete.map((id) => {
      historicPendingCommData?.result?.map((pendingHistory: PendingCommunicationsHistory) => {
        if (id === pendingHistory.communicationID) {
          const message =
            ' ' +
            String(pendingHistory?.serviceName) +
            ' ' +
            t('common', 'of') +
            ' ' +
            moment(pendingHistory?.serviceDate).format('DD/MM') +
            ' ';
          warningMessage.push(message);
        }
      });
    });
    return warningMessage.toString();
  };

  const handleMessageToDelete = (event: CheckboxChangeEvent, record: PendingCommunicationsHistory) => {
    const newMessagesIDsToDelete: number[] = [...messagesToDelete];
    if (event.target.checked) {
      newMessagesIDsToDelete.push(record.communicationID);
      setmessagesToDelete(newMessagesIDsToDelete);
    } else {
      setmessagesToDelete(newMessagesIDsToDelete.filter((id) => id !== record.communicationID));
    }
  };

  return (
    <div>
      <Modal
        title={t('Modal', 'PRODUCT_CODE_MODAL_TITLE')}
        footer={null}
        open={modalVisible}
        onCancel={() => onModalConfirm(false)}
      >
        <ModalContentConfirm
          title={
            <div>
              {t('Modal', 'HISTORIC_PENDING_MESSAGE_CONFIRM')}
              <StyledListBoldWrapper>{htmlParser(prepareWarningMessage())}</StyledListBoldWrapper>
            </div>
          }
          onConfirm={onModalConfirm}
        />
      </Modal>

      <MessageModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          dispatch(historicPendingCommunicationActions.fetchHistoricPendingCommunication(filterValues));
        }}
        errorMessage={error && isRequestError(error) ? undefined : error}
      />
      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('filtre', 'HISTORISATION_PENDING_TABLE')}</StyledMainTitle>
        </StyledListTitleWrapper>
        <HistorisationPendingFilterForm
          situations={situations}
          senders={senders}
          data={historicPendingCommData}
          urlSearchParams={urlSearchParams || {}}
          onFilterChange={setFilterValues}
        />
        {historicPendingCommStatus === 'success' && (
          <HistorisationPendingList
            data={historicPendingCommData}
            onClickDelete={onDeleteHandler}
            handleSelectMessages={handleMessageToDelete}
            canWriteDefferedCommunications={canWriteDefferedCommunications}
            filterValues={filterValues}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};

export default HistorisationPending;
